/**
 * Renko Chart Calculator — motor "HTML antigo" (port fiel do ChartIQ)
 *
 * Port de `CIQ.ChartEngine.calculateRenkoBars` (HtmlChart/wwwroot/js/chart/
 * chartiq.js, decodificado do bundle minificado) — issue #82, mesmo tratamento
 * do Ponto & Figura. Convive em paralelo com o motor legado (`renko.jsx`):
 * `renkoConfig.engine = "chartiq"` seleciona este; ausente/"legacy" usa o atual.
 *
 * Semântica ChartIQ:
 * - Preço = Close apenas.
 * - Seed alinhado à grade: base = floor(open0/brick)·brick; thresholds base±brick.
 * - Brick DOWN quando close ≤ lo: [lo+brick → lo]; depois hi=lo+2·brick, lo−=brick.
 *   Brick UP quando close ≥ hi: [hi−brick → hi]; depois l0=hi−2·brick, hi+=brick.
 *   (reversão efetiva de 2 bricks; while até o close ficar dentro da janela)
 * - Brick FINAL PARCIAL (em formação): close estritamente dentro da janela vira
 *   brick até o próprio close (fullyFormed:false) — igual ao HTML antigo.
 * - Auto brick = floor(10000·range/(altura/30))/10000 (≈1 brick por 30px). Como o
 *   calculador não conhece o painel, usa NOMINAL_PANEL_HEIGHT=800 (documentado —
 *   no ChartIQ real o auto muda com resize/zoom, então auto nunca é comparável
 *   1:1 entre ambientes; paridade exata = brick fixo).
 * - Desvio deliberado: ChartIQ zera Volume dos bricks; aqui acumulamos o volume
 *   das barras (não afeta a geometria validada; preserva o painel de volume).
 */

import { Renko as defaultOptions } from "./defaultOptionsForComputation";
import { calculateATR } from "./renko";

// Mesma política do P&F: teto de bricks contra freeze com brick minúsculo,
// warns warn-once (calculadora roda a cada quote batch).
const MAX_TOTAL_BRICKS = 10000;
const NOMINAL_PANEL_HEIGHT = 800;

const warned = {};
function warnOnce(key, msg) {
  if (!warned[key]) {
    warned[key] = true;
    console.warn(msg);
  }
}

// Fórmula de auto brick do ChartIQ (calculateRenkoBars):
//   floor(10000 · range / (height/30)) / 10000, com piso range/height (1px).
// range = minMax de close/high/low das últimas 300 barras.
export function chartIQBrickSize(rawData, panelHeight = NOMINAL_PANEL_HEIGHT) {
  const sampleSize = Math.min(300, rawData.length);
  const recent = rawData.slice(rawData.length - sampleSize);
  let min = Infinity;
  let max = -Infinity;
  recent.forEach((d) => {
    [d.close, d.high, d.low].forEach((p) => {
      if (Number.isFinite(p)) {
        if (p < min) min = p;
        if (p > max) max = p;
      }
    });
  });
  const range = max - min;
  if (!isFinite(range) || range <= 0) return 0.5;
  const brick = Math.floor((10000 * range) / (panelHeight / 30)) / 10000;
  return Math.max(brick, range / panelHeight) || 0.5;
}

// Prioridade ano > trimestre > mês > semana (mesma dos outros calculadores)
function applyFlags(brick, src, dt) {
  if (src.startOfYear && !brick.startOfYear) {
    brick.startOfYear = true;
    brick.startOfQuarter = src.startOfQuarter;
    brick.startOfMonth = src.startOfMonth;
    brick.startOfWeek = src.startOfWeek;
    brick.date = dt;
  } else if (src.startOfQuarter && brick.startOfQuarter !== true) {
    brick.startOfQuarter = true;
    brick.startOfMonth = src.startOfMonth;
    brick.startOfWeek = src.startOfWeek;
    brick.date = dt;
  } else if (src.startOfMonth && brick.startOfMonth !== true) {
    brick.startOfMonth = true;
    brick.startOfWeek = src.startOfWeek;
    brick.date = dt;
  } else if (src.startOfWeek && brick.startOfWeek !== true) {
    brick.startOfWeek = true;
    brick.date = dt;
  }
}

export default function () {
  let options = defaultOptions;
  let dateAccessor = (d) => d.date;
  let dateMutator = (d, date) => {
    d.date = date;
  };

  function calculator(rawData) {
    if (!rawData || rawData.length === 0) {
      warnOnce("noData", "Renko(chartiq): No data to process");
      return [];
    }

    const {
      mode = "auto",
      brickSize: configBrickSize,
      atrPeriod = 14,
      atrMultiplier = 1.0,
    } = options;

    // Resolução do brick: mesmos modos do legado; auto = fórmula ChartIQ
    let brick;
    if (mode === "atr") {
      const atrValue = calculateATR(rawData, atrPeriod);
      brick =
        atrValue && isFinite(atrValue) && atrValue > 0
          ? atrValue * atrMultiplier
          : chartIQBrickSize(rawData);
    } else if (configBrickSize && configBrickSize > 0) {
      brick = configBrickSize;
    } else {
      brick = chartIQBrickSize(rawData);
    }
    if (!isFinite(brick) || brick <= 0) brick = 0.5;

    const round8 = (v) => Number(v.toFixed(8)); // igual ao h3 do ChartIQ

    const bricks = [];
    let lo = null; // threshold inferior (l0)
    let hi = null; // threshold superior (K$)
    let c7 = null; // barra que abriu o brick pendente (dá as datas, como no ChartIQ)
    let c7Index = 0;
    let pendingVolume = 0;
    let pendingFlags = null;
    let totalBricks = 0;
    let overflow = false;

    const collectPending = (d, dt) => {
      pendingFlags = pendingFlags || { date: dt };
      pendingFlags.startOfYear = pendingFlags.startOfYear || d.startOfYear;
      pendingFlags.startOfQuarter =
        pendingFlags.startOfQuarter || d.startOfQuarter;
      pendingFlags.startOfMonth = pendingFlags.startOfMonth || d.startOfMonth;
      pendingFlags.startOfWeek = pendingFlags.startOfWeek || d.startOfWeek;
    };

    // h3 do ChartIQ: brick {Open, Close, High=max, Low=min}, datas do c7
    const emitBrick = (open, close, d, idx, fullyFormed) => {
      totalBricks += 1;
      if (totalBricks > MAX_TOTAL_BRICKS) {
        overflow = true;
        return;
      }
      const o = round8(open);
      const c = round8(close);
      const src = c7 || d;
      const dt = dateAccessor(src);
      const b = {
        open: o,
        close: c,
        high: Math.max(o, c),
        low: Math.min(o, c),
        direction: c > o ? 1 : -1,
        volume: pendingVolume,
        from: c7 ? c7Index : idx,
        to: idx,
        fromDate: dt,
        toDate: dateAccessor(d),
        date: dt,
        startOfYear: false,
        startOfQuarter: false,
        startOfMonth: false,
        startOfWeek: false,
        fullyFormed,
      };
      if (pendingFlags) {
        applyFlags(b, pendingFlags, pendingFlags.date);
        pendingFlags = null;
      }
      applyFlags(b, src, dt);
      dateMutator(b, b.date);
      bricks.push(b);
      pendingVolume = 0;
    };

    for (let i = 0; i < rawData.length && !overflow; i++) {
      const d = rawData[i];
      if (!d) continue;
      const close = d.close;
      if (!isFinite(close)) {
        collectPending(d, dateAccessor(d));
        continue;
      }
      pendingVolume += d.volume || 0;

      if (lo === null && hi === null) {
        // Seed: snap do primeiro open à grade do brick (fallback close)
        const y9 = d.open || d.open === 0 ? d.open : close;
        const snapped = Math.floor(y9 / brick) * brick;
        const base = isNaN(snapped) ? y9 : snapped;
        lo = base - brick;
        hi = base + brick;
      }

      // while: gera todos os bricks que o close deste bar cruzou
      for (;;) {
        if (!c7) {
          c7 = d;
          c7Index = i;
        }
        if (close <= lo) {
          emitBrick(lo + brick, lo, d, i, true); // brick de baixa
          hi = lo + 2 * brick;
          lo -= brick;
          c7 = null;
        } else if (close >= hi) {
          emitBrick(hi - brick, hi, d, i, true); // brick de alta
          lo = hi - 2 * brick;
          hi += brick;
          c7 = null;
        } else {
          break;
        }
        if (overflow) break;
      }
    }

    if (overflow) {
      // chave SEM o brick bruto: em auto/ATR o float muda a cada quote batch e
      // viraria warn-por-tick + crescimento sem fim do mapa `warned`
      warnOnce(
        "overflow",
        `Renko(chartiq): mais de ${MAX_TOTAL_BRICKS} bricks (brick=${brick} pequeno demais para o range) — gráfico vazio`,
      );
      return [];
    }

    // Brick final parcial (em formação) — igual ao ChartIQ
    const last = rawData[rawData.length - 1];
    const lastClose = last?.close;
    if (isFinite(lastClose) && lo !== null) {
      if (lo < lastClose && lo + brick > lastClose) {
        emitBrick(lo + brick, lastClose, last, rawData.length - 1, false);
      } else if (hi > lastClose && hi - brick < lastClose) {
        emitBrick(hi - brick, lastClose, last, rawData.length - 1, false);
      }
    }

    return bricks;
  }

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };
  calculator.dateMutator = function (x) {
    if (!arguments.length) return dateMutator;
    dateMutator = x;
    return calculator;
  };
  calculator.dateAccessor = function (x) {
    if (!arguments.length) return dateAccessor;
    dateAccessor = x;
    return calculator;
  };

  return calculator;
}
